import { type LucideIcon, RefreshCw, TriangleAlert, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Button } from './Button';

/**
 * Pusty stan (DESIGN §7): spokojnie, jedno zdanie i najwyżej jedna akcja.
 * `compact` — w listach na stronie (np. „Plan na dziś”), bez ikony w kółku.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <div className={cn('flex min-h-[52px] items-center gap-3 border-b border-line py-3', className)}>
        {Icon && <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />}
        <div className="min-w-0 flex-1">
          <p className="text-body-s text-fg-secondary">{title}</p>
          {description && <p className="text-caption text-fg-muted">{description}</p>}
        </div>
        {action}
      </div>
    );
  }
  return (
    <div className={cn('flex flex-col items-center px-6 py-16 text-center', className)}>
      {Icon && (
        <span className="mb-4 inline-flex size-10 items-center justify-center rounded-full bg-surface text-fg-muted">
          <Icon size={20} strokeWidth={1.75} aria-hidden />
        </span>
      )}
      <p className="text-body-s font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-[420px] text-body-s text-fg-muted">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Błąd wczytywania z ponowieniem. */
export function ErrorState({
  title = 'Nie udało się wczytać danych',
  description,
  onRetry,
  retrying = false,
  offline = false,
  compact = false,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
  /** Ikona i domyślny opis braku połączenia. */
  offline?: boolean;
  compact?: boolean;
  className?: string;
}) {
  const Icon = offline ? WifiOff : TriangleAlert;
  const retry = onRetry && (
    <Button variant="secondary" size={compact ? 's' : 'm'} icon={RefreshCw} onClick={onRetry} loading={retrying}>
      Spróbuj ponownie
    </Button>
  );
  if (compact) {
    return (
      <div role="alert" className={cn('flex min-h-[52px] items-center gap-3 border-b border-line py-3', className)}>
        <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-danger" />
        <p className="min-w-0 flex-1 text-body-s text-fg-secondary">{title}</p>
        {retry}
      </div>
    );
  }
  return (
    <div role="alert" className={cn('flex flex-col items-center px-6 py-16 text-center', className)}>
      <span
        className={cn(
          'mb-4 inline-flex size-10 items-center justify-center rounded-full',
          offline ? 'bg-surface text-fg-muted' : 'bg-danger-subtle text-danger',
        )}
      >
        <Icon size={20} strokeWidth={1.75} aria-hidden />
      </span>
      <p className="text-body-s font-medium text-fg">{title}</p>
      <p className="mt-1 max-w-[420px] text-body-s text-fg-muted">
        {description ??
          (offline
            ? 'Sprawdź internet. Wrócimy do pracy, gdy połączenie się pojawi.'
            : 'To może być chwilowy problem z serwerem.')}
      </p>
      {retry && <div className="mt-5">{retry}</div>}
    </div>
  );
}

/** Szkielet ładowania (bg/elevated, delikatne pulsowanie). */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn('block animate-pulse rounded-md bg-elevated', className)} />;
}

/** Szkielet listy: n wierszy po 52 px. */
export function ListSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div role="status" aria-label="Wczytywanie" className={className}>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: statyczne wiersze szkieletu
        <div key={i} className="flex h-[52px] items-center gap-3.5 border-b border-line">
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className={cn('h-3.5', i % 2 ? 'w-2/5' : 'w-3/5')} />
          <span className="flex-1" />
          <Skeleton className="h-3.5 w-12" />
        </div>
      ))}
    </div>
  );
}
